/**
 * Layout constants for calendar dimensions and positioning
 * Centralizes all magic numbers to make the codebase more maintainable
 */

// ============================================================================
// CELL DIMENSIONS
// ============================================================================

/** Width of a single day cell in pixels */
export const CELL_WIDTH = 28;

/** Minimum height of a single day cell in pixels (when no events/milestones) */
export const CELL_MIN_HEIGHT = 36;

/** Base height before events (space for day number + padding) */
export const CELL_BASE_HEIGHT = 16;

/** Bottom padding below last element in a cell */
export const CELL_BOTTOM_PADDING = 4;

/** Width of the month label column in pixels */
export const MONTH_LABEL_WIDTH = 40;

// ============================================================================
// HEADER DIMENSIONS
// ============================================================================

/** Height of the day-of-week header row in pixels */
export const HEADER_HEIGHT = 24;

// ============================================================================
// EVENT BAR LAYOUT
// ============================================================================

/** Height of an event bar in pixels */
export const EVENT_BAR_HEIGHT = 16;

/** Vertical gap between event bars in pixels */
export const EVENT_BAR_VERTICAL_GAP = 2;

/** Total height per event bar row (height + gap) */
export const EVENT_BAR_ROW_HEIGHT = EVENT_BAR_HEIGHT + EVENT_BAR_VERTICAL_GAP;

/** Top offset for the first event bar row within a month */
export const EVENT_BAR_TOP_OFFSET = 16;

/** Horizontal extension for zigzag continuation indicators */
export const ZIGZAG_EXTENSION = 6;

/** Horizontal padding inside event bars */
export const EVENT_BAR_HORIZONTAL_PADDING = 2;

// ============================================================================
// MILESTONE LAYOUT
// ============================================================================

/** Width of a milestone marker in pixels */
export const MILESTONE_WIDTH = 18;

/** Height of a milestone marker in pixels */
export const MILESTONE_HEIGHT = 16;

/** Horizontal offset to center milestone in cell: (CELL_WIDTH - MILESTONE_WIDTH) / 2 */
export const MILESTONE_HORIZONTAL_OFFSET = (CELL_WIDTH - MILESTONE_WIDTH) / 2;

/** Top position for milestone when no events are present (row === -1) */
export const MILESTONE_TOP_NO_EVENTS = 20;

/** Additional offset for milestones positioned below events (centers milestone with event bars) */
export const MILESTONE_TOP_WITH_EVENTS_OFFSET = 15;

// ============================================================================
// ZOOM SETTINGS
// ============================================================================

/** Minimum zoom level (percentage) */
export const MIN_ZOOM = 25;

/** Maximum zoom level (percentage) */
export const MAX_ZOOM = 500;

/** Minimum zoom for initial calculation */
export const INITIAL_ZOOM_MIN = 50;

/** Maximum zoom for initial calculation */
export const INITIAL_ZOOM_MAX = 200;

/** Zoom step size when using scroll wheel */
export const ZOOM_DELTA = 10;

/** Rounding step for initial zoom calculation */
export const ZOOM_ROUND_STEP = 1;

// ============================================================================
// CALENDAR LAYOUT
// ============================================================================

/** Number of columns in the calendar (max days in a month) */
export const TOTAL_COLUMNS = 37;

/** Padding on right side of calendar */
export const CALENDAR_RIGHT_PADDING = 48;

/** Additional margin for calendar width calculation */
export const CALENDAR_MARGIN = 20;

/** Padding for wrapper element */
export const WRAPPER_PADDING = 24;

/** Padding multiplier for wrapper dimensions (padding on both sides) */
export const WRAPPER_PADDING_MULTIPLIER = 2;

/** Wrapper padding total for width/height */
export const WRAPPER_PADDING_TOTAL = WRAPPER_PADDING * WRAPPER_PADDING_MULTIPLIER;

// ============================================================================
// TIMING CONSTANTS
// ============================================================================

/** Delay before showing tooltips in milliseconds */
export const TOOLTIP_DELAY = 200;

/** Debounce delay for saving to LocalStorage in milliseconds */
export const SAVE_DEBOUNCE_MS = 500;

/** Distance in pixels mouse must move before activating drag */
export const DRAG_THRESHOLD = 5;

/** Delay before clearing drag state after drop in milliseconds */
export const DRAG_CLEAR_DELAY = 100;
